import { useCallback, useEffect, useLayoutEffect, useState, useMemo, useRef } from 'react';
import { META_OPTIMIZER_ENABLED } from '../utils/portfolioDesign';
import useOptimizerBaseline from './useOptimizerBaseline';
import useOptimizerResultState from './useOptimizerResultState';
import useOptimizerEditorState, { buildOptimizerDefaultConstraints } from './useOptimizerEditorState';
import useOptimizerExecution from './useOptimizerExecution';
import useOptimizerPortfolioPersistence from './useOptimizerPortfolioPersistence';
import {
    MAX_SHARPE_OBJECTIVE,
} from '../utils/optimizerObjectives';
import { callBackend } from '../utils/api';
import { useLoadSavedOptimizedPortfolio } from "./useLoadSavedOptimizedPortfolio";
import { useViewModeBaseAndMetrics, projectOptimizerBaseRows } from "../components/optimizer/useViewModeBaseAndMetrics";
import { getEffectiveFactorDataModel } from '../utils/factorDataOptions';
import { normalizeCogniveId } from '../components/optimizer/optimizerIdentityHelpers';
import {
    includedSelection,
    NO_OPTIMIZER_SELECTIONS,
} from '../components/optimizer/optimizerBaselineInclusion';
import {
    optimizerErrorMessage,
    requireOptimizerPortfolioList,
    projectOptimizerRunPositions,
} from '../components/optimizer/optimizerPortfolioMutationContract';
import {
    getOrLoadOptimizerSummaries,
    invalidateOptimizerSummariesCache,
} from '../components/dashboard/orchestration/optimizerSummariesCache';
import {
    createOptimizerOperationId,
    canonicalJsonObject,
} from '../utils/optimizerConstraintEditor';
import {
    clearOptimizerContractCache,
    getOrLoadOptimizerContract,
    optimizerContextStatusPresentation,
    optimizerV2VersionCommand,
    requireOptimizerV2PortfolioSummariesResponse,
    requireOptimizerV2RunResponse,
} from '../utils/optimizerGenericGroupContract';
import {
    buildOptimizerRunCommand,
    filterGroupConstraintsToCatalog,
    getPosSizingOptimizerRunBlocker,
    optimizerBaselineForViewMode,
    optimizerRunPreflightError,
    optimizerReceiptRunConfiguration,
} from '../utils/optimizerRunCommand';

export { getOptimizerFinancialInputError, getPosSizingOptimizerRunBlocker } from '../utils/optimizerRunCommand';

const EMPTY_OPTIMIZER_PORTFOLIOS = Object.freeze([]);

const EMPTY_OPTIMIZER_MUTATION_BARRIERS = Object.freeze({});
const optimizerPortfolioBarrierKey = ({ portfolioId = null, portfolioName = null } = {}) => (
    portfolioId != null
        ? `id:${String(portfolioId)}`
        : `name:${String(portfolioName ?? '').trim()}`
);

const optimizerPortfolioMatchesBarrier = (portfolio, barrier) => (
    (barrier?.portfolioId != null
        ? String(portfolio?.id) === String(barrier.portfolioId)
        : String(portfolio?.name ?? '').trim() === barrier?.portfolioName)
    && (
        barrier?.portfolioVersion == null
        || (portfolio?.portfolioVersion ?? portfolio?.portfolio_version)
            === barrier.portfolioVersion
    )
    && (
        barrier?.receiptId == null
        || (portfolio?.receiptId ?? portfolio?.receipt_id) === barrier.receiptId
    )
);

const optimizerMutationBarrierIsReconciled = (barrier, portfolios, allowUnknownResolution) => {
    const present = portfolios.some((portfolio) => optimizerPortfolioMatchesBarrier(portfolio, barrier));
    if (barrier?.outcome === 'unknown') {
        return allowUnknownResolution && barrier.wasPresent !== present;
    }
    if (barrier?.outcome !== 'committed') return false;
    if (barrier.reconcileOnList === false) return false;
    return barrier.expected === 'absent' ? !present : present;
};

function optimizerOverlayValue(overlay, row) {
    const cogniveId = normalizeCogniveId(row?.cognive_id);
    return cogniveId ? (overlay?.[cogniveId] ?? null) : null;
}

// A displayed result keeps the rows it was run with, so a live data refresh does
// not make it out of date, but the live selection still counts as an edit.
function withLiveSelection(pinnedRows, liveRows) {
    const selection = new Map((Array.isArray(liveRows) ? liveRows : []).map((row) => [
        normalizeCogniveId(row?.cognive_id),
        row?.checkBoxEnable === true,
    ]));
    return pinnedRows.map((row) => {
        const cogniveId = normalizeCogniveId(row?.cognive_id);
        return selection.has(cogniveId)
            ? { ...row, checkBoxEnable: selection.get(cogniveId) }
            : row;
    });
}

function projectOptimizerTickerRows(
    baseRows,
    optimizedGmvsMap,
    receiptBaselineGmvsMap = null,
    receiptInputRows = null,
) {
    const pinnedRowsByCogniveId = new Map(
        (Array.isArray(receiptInputRows) ? receiptInputRows : []).map((row) => [
            normalizeCogniveId(row?.cognive_id),
            row,
        ]),
    );
    const rows = baseRows.map((row) => {
        const optimalGmv = optimizerOverlayValue(optimizedGmvsMap, row);
        const cogniveId = normalizeCogniveId(row?.cognive_id);
        const receiptPositionGmv = optimizerOverlayValue(
            receiptBaselineGmvsMap,
            row,
        );
        const pinnedPositionGmv = pinnedRowsByCogniveId.get(cogniveId)?.positionGmv;
        const positionGmv = Array.isArray(receiptInputRows)
            ? receiptPositionGmv ?? pinnedPositionGmv ?? row.positionGmv ?? 0
            : row.positionGmv ?? 0;
        const hasRunValues = optimalGmv != null && positionGmv != null;
        return {
            ...row,
            positionGmv,
            optimalGmv,
            runBaselineGmv: hasRunValues ? positionGmv : null,
            tradeGmv: hasRunValues ? optimalGmv - positionGmv : null,
            deltaGmv: optimalGmv == null || positionGmv == null || positionGmv === 0
                ? null
                : (optimalGmv - positionGmv) / Math.abs(positionGmv),
        };
    });
    if (optimizedGmvsMap) {
        rows.sort((left, right) => (
            left.checkBoxEnable === right.checkBoxEnable
                ? 0
                : left.checkBoxEnable ? -1 : 1
        ));
    }
    return rows;
}

function normalizeConstraintsForRun(constraints) {
    return {
        design: constraints?.design ?? null,
        // Only Max Sharpe states a skill; other identities are unchanged.
        ...(constraints?.expected_return_policy
            ? { expected_return_policy: constraints.expected_return_policy } : {}),
        static_constraints: { ...(constraints?.static_constraints || {}) },
        dynamic_constraints: { ...(constraints?.dynamic_constraints || {}) },
        // Sorted so the same per-name controls always give the same identity.
        security_controls: Object.fromEntries(
            Object.entries(constraints?.security_controls || {}).sort(
                ([left], [right]) => (left < right ? -1 : 1),
            ),
        ),
    };
}

function normalizeConstraintTogglesForRun(constraintToggles) {
    return {
        static_constraints: { ...(constraintToggles?.static_constraints || {}) },
    };
}

export function buildOptimizerRunInputIdentity({
    userUid,
    activeView,
    viewMode,
    tickerData,
    constraints,
    constraintToggles,
    groupConstraints = [],
    objective,
    selectedFactorModel,
    selectedBetaSource,
    factorSnapshotDate,
    factorDataModel,
    optimizerIdioVolSource,
    posSizingContextDocumentSha256,
}) {
    const securityData = projectOptimizerRunPositions(tickerData, viewMode);
    return JSON.stringify({
        user_uid: userUid ?? null,
        active_view: activeView ?? null,
        view_mode: viewMode ?? null,
        security_data: securityData,
        constraints: normalizeConstraintsForRun(constraints),
        constraint_toggles: normalizeConstraintTogglesForRun(constraintToggles),
        group_constraints: groupConstraints,
        objective: objective ?? null,
        factor_model: selectedFactorModel ?? null,
        beta_source: selectedBetaSource ?? null,
        factor_snapshot_date: factorSnapshotDate ?? null,
        factor_data_model: factorDataModel ?? null,
        idio_vol_source: optimizerIdioVolSource ?? null,
        pos_sizing_context_document_sha256: viewMode === 'pos-sizing'
            ? posSizingContextDocumentSha256 ?? null
            : null,
    });
}

export function mapOptimizerPortfolio(portfolio) {
    const contextPresentation = portfolio?.type === 'opt'
        ? optimizerContextStatusPresentation(portfolio?.context_status, portfolio)
        : null;
    return {
        id: portfolio?.id,
        value: portfolio?.name,
        type: portfolio?.type,
        factorModel: portfolio?.factor_model ?? null,
        factorDataModel: portfolio?.factor_data_model ?? null,
        factorSnapshotDate: portfolio?.factor_snapshot_date ?? null,
        baselineSourceMode: portfolio?.baseline_source_mode ?? null,
        optimizerIdioVolSource: portfolio?.optimizer_idio_vol_source ?? null,
        optimizerBetaSource: portfolio?.optimizer_beta_source ?? null,
        baselinePosSizingStateHash: portfolio?.baseline_pos_sizing_state_hash ?? null,
        currentPosSizingStateHash: portfolio?.current_pos_sizing_state_hash ?? null,
        isBaselineStale: Boolean(portfolio?.is_baseline_stale),
        staleReasons: portfolio?.stale_reasons ?? [],
        savedPriceWatermark: portfolio?.saved_price_watermark ?? null,
        lastRefreshedAt: portfolio?.last_refreshed_at ?? null,
        receiptStatus: portfolio?.receipt_status,
        receiptId: portfolio?.receipt_id ?? null,
        receiptContractVersion: portfolio?.receipt_contract_version ?? null,
        portfolioVersion: portfolio?.portfolio_version ?? null,
        lastOptimizerPortfolioVersion: portfolio?.last_optimizer_portfolio_version ?? null,
        modifiedSinceOptimization: portfolio?.modified_since_optimization === true,
        compatibility: portfolio?.compatibility ?? null,
        contextStatus: portfolio?.context_status ?? null,
        contextPresentation,
    };
}

const mapWhatIfPortfolios = ({ portfolios, mutationBarriers }) => portfolios.map((portfolio) => ({
    ...mapOptimizerPortfolio(portfolio),
    optimizerMutationBlocked: Object.values(mutationBarriers)
        .some((barrier) => optimizerPortfolioMatchesBarrier(portfolio, barrier)),
}));

const checkedOptimizerBaselineGmv = rows => {
    const total = rows.reduce((sum, row) => {
        if (row?.checkBoxEnable !== true) return sum;
        const value = Math.abs(Number(row?.positionGmv));
        return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    return total > 0 ? total : null;
};
const actualMembershipSignature = rows => [...new Set((rows || [])
    .map(row => normalizeCogniveId(row?.cognive_id)).filter(Boolean))].sort().join('|');
export const buildOptimizerBaselineInputKey = ({ userScopeKey, watchlistVersion, viewMode,
    selectedFactorModel, selectedBetaSource, factorSnapshotDate, factorDataModel, optimizerIdioVolSource,
    selectedAxiomaModel, selectedBarraModel, posSizingOptimizerContext, aum, gmv, rows, factorSliceScopeKey }) => JSON.stringify([
    userScopeKey, watchlistVersion, viewMode, selectedFactorModel, selectedBetaSource, factorSnapshotDate, factorDataModel,
    optimizerIdioVolSource, selectedAxiomaModel, selectedBarraModel,
    posSizingOptimizerContext?.context_document_sha256, aum, gmv,
    rows.filter(row => row.checkBoxEnable === true), factorSliceScopeKey,
]);

export function useOptimizer(
    activeView,
    combinedHoldings,
    posSizingData,
    selectedFactorModel,
    selectedBetaSource,
    factorSnapshotDate,
    factorDataModel,
    optimizerIdioVolSource,
    industries,
    aum,
    selectedAxiomaModel,
    selectedBarraModel,
    userUid,
    posSizingOptimizerContext = null,
    onPosSizingContextChanged = null,
    watchlistVersion = 0,
    optimizerWorkspaceAvailable = true,
    onWhatIfPortfoliosCommitted = null,
    factorSliceScopeKey = null,
    exclusiveCoreLoadOwnerRef = null,
) {
    const optimizerUserScopeKey = String(userUid ?? '');
    const optimizerUserScopeRef = useRef({ key: optimizerUserScopeKey });
    if (optimizerUserScopeRef.current.key !== optimizerUserScopeKey) {
        optimizerUserScopeRef.current = { key: optimizerUserScopeKey };
    }
    const optimizerUserScope = optimizerUserScopeRef.current;
    const optimizerWatchlistVersionRef = useRef(watchlistVersion);
    optimizerWatchlistVersionRef.current = watchlistVersion;
    useEffect(() => {
        clearOptimizerContractCache({ preserveUserUid: optimizerUserScopeKey });
    }, [optimizerUserScopeKey]);
    const [optimizerPortfolioListState, setOptimizerPortfolioListState] = useState({
        userUid: optimizerUserScopeKey,
        portfolios: [],
        error: null,
        mutationBarriers: {},
    });
    const optimizerPortfolioListStateRef = useRef(optimizerPortfolioListState);
    optimizerPortfolioListStateRef.current = optimizerPortfolioListState;
    const onWhatIfPortfoliosCommittedRef = useRef(onWhatIfPortfoliosCommitted);
    onWhatIfPortfoliosCommittedRef.current = onWhatIfPortfoliosCommitted;
    const commitOptimizerPortfolioListState = useCallback((userScope, updater) => {
        if (optimizerUserScopeRef.current !== userScope) return null;
        const previous = optimizerPortfolioListStateRef.current.userUid === userScope.key
            ? optimizerPortfolioListStateRef.current
            : {
                userUid: userScope.key,
                portfolios: [],
                error: null,
                mutationBarriers: {},
            };
        const next = updater(previous);
        optimizerPortfolioListStateRef.current = next;
        setOptimizerPortfolioListState(next);
        if (next.portfolios !== previous.portfolios) {
            onWhatIfPortfoliosCommittedRef.current?.(
                mapWhatIfPortfolios(previous),
                mapWhatIfPortfolios(next),
            );
        }
        return next;
    }, []);
    // The list committed for the current user, readable before React renders it.
    const readWhatIfPortfolios = useCallback(() => {
        const listState = optimizerPortfolioListStateRef.current;
        return listState.userUid === optimizerUserScopeRef.current.key
            ? mapWhatIfPortfolios(listState)
            : [];
    }, []);
    const optimizerPortfolioStateForUser = (
        optimizerPortfolioListState.userUid === optimizerUserScopeKey
            ? optimizerPortfolioListState
            : {
                portfolios: EMPTY_OPTIMIZER_PORTFOLIOS,
                error: null,
                mutationBarriers: EMPTY_OPTIMIZER_MUTATION_BARRIERS,
            }
    );
    const listOfWhatIfPortfolios = useMemo(
        () => mapWhatIfPortfolios({
            portfolios: optimizerPortfolioStateForUser.portfolios,
            mutationBarriers: optimizerPortfolioStateForUser.mutationBarriers,
        }),
        [optimizerPortfolioStateForUser.portfolios, optimizerPortfolioStateForUser.mutationBarriers],
    );
    const { state: resultState, actions: resultActions, acceptedStateRef: acceptedResultRef,
        loadedFactorContext, loadedFactorResult } = useOptimizerResultState(factorSliceScopeKey ?? optimizerUserScopeKey);
    const {
        riskMetricsActPort, riskMetricsOptPort, riskMetricsDeltaPort,
        attributeRiskMetrics, constraintEvaluation, resultIssuedAt, solverStatus,
        displayedExpectedReturnPolicy, risksByFactor, optimizedGmvsMap,
        receiptBaselineGmvsMap, receiptDisplaySource, displayedResultIdentity,
        showPortfolioMetrics, constraintSummary, completedOptimizerRun,
    } = resultState;
    const { setShowPortfolioMetrics } = resultActions;

    const [tickerData, setTickerDataState] = useState([]);
    const [baseRows, setBaseRows] = useState([]);
    const tickerDataRef = useRef(tickerData);
    tickerDataRef.current = tickerData;
    const [optimizerIsRunning, setOptimizerIsRunning] = useState(false);
    const execution = useOptimizerExecution(optimizerUserScopeKey);
    const [errorOptimization, setErrorOptimization] = useState(null);
    const [viewModeLoading, setViewModeLoading] = useState(false);


    const clearOptimizedOverlay = resultActions.clearOverlay;
    const { state: editorState, actions: editorActions } = useOptimizerEditorState(aum);
    const { constraints, constraintToggles, groupConstraints, objective, gmvBudgetMode } = editorState;
    // The GMV budget follows the checked names' baseline GMV until the user
    // types a budget or a saved configuration is installed. A displayed result
    // is measured on its pinned rows, so a data refresh alone cannot change it.
    const checkedBaselineGmv = useMemo(() => {
        const rows = receiptDisplaySource != null && acceptedResultRef.current.receiptInputRows
            ? withLiveSelection(acceptedResultRef.current.receiptInputRows, tickerData)
            : tickerData;
        return checkedOptimizerBaselineGmv(rows);
    }, [receiptDisplaySource, tickerData]);
    const checkedBaselineGmvRef = useRef(checkedBaselineGmv);
    checkedBaselineGmvRef.current = checkedBaselineGmv;
    useEffect(() => {
        if (gmvBudgetMode !== 'auto') return;
        editorActions.syncAutomaticGmv(checkedBaselineGmv);
    }, [checkedBaselineGmv, editorActions, gmvBudgetMode]);
    const markGmvBudgetManual = editorActions.markGmvManual;
    const [optimizerV2Capability, setOptimizerV2Capability] = useState(null);
    const [optimizerAttributeCatalog, setOptimizerAttributeCatalog] = useState(null);
    const optimizerGroupInitializationRef = useRef(null);
    const preparedContractScopeRef = useRef(null);



    const [optimalPortfolioOptimizer, setOptimalPortfolioOptimizer] = useState('');
    const [optStatus, setOptStatus] = useState(null);
    // Optimizer startup must not depend on the Position Sizing tab having
    // hydrated its saved draft. Current/Actual is supplied by the app-wide
    // risk bootstrap and is therefore the only safe cold-start baseline.
    const [viewMode, setViewMode] = useState('normal');
    const viewModeRef = useRef(viewMode);
    viewModeRef.current = viewMode;
    // Current and Pos Sizing each keep their own checked securities; null checks
    // that baseline's members.
    const [optimizerSelections, setOptimizerSelections] = useState(NO_OPTIMIZER_SELECTIONS);
    const keepOptimizerSelection = useCallback((mode, selection) => {
        setOptimizerSelections((previous) => (
            previous[mode] === selection ? previous : { ...previous, [mode]: selection }
        ));
    }, []);
    const clearOptimizerSelection = useCallback((mode) => {
        keepOptimizerSelection(mode, null);
    }, [keepOptimizerSelection]);
    // The saved portfolio whose baseline, checked names and limits are
    // installed in the tab; it keeps the portfolio's saved AUM. New run, a
    // baseline switch, or another load clears it.
    const [experimentGeneration, setExperimentGeneration] = useState(0);
    const [loadedOptimizerPortfolio, setLoadedOptimizerPortfolio] = useState(null);
    // A loaded portfolio's checked names and result wait for its baseline's
    // rows; switching to Position Sizing loads those rows first.
    const pendingSavedInstallRef = useRef(null);
    const [pendingSavedInstallVersion, setPendingSavedInstallVersion] = useState(0);
    const optimizerRunGenerationRef = useRef(0);
    const optimizerRunInputIdentityRef = useRef('');
    const optimizerUiUserScopeKeyRef = useRef(null);
    const optimizerListRequestGenerationRef = useRef(0);
    const optimizerMutationGenerationRef = useRef(0);
    const optimizerMutationBarrierRevisionRef = useRef(0);
    const installSavedReceiptAllocationOverlay = resultActions.installSavedOverlay;
    // Install a loaded portfolio's baseline now; its checked names and result
    // overlay follow once that baseline's rows exist.
    const installSavedOptimizerConfiguration = useCallback((configuration, overlay) => {
        setLoadedOptimizerPortfolio(configuration.loadedPortfolio);
        pendingSavedInstallRef.current = {
            viewMode: configuration.viewMode,
            selectedCogniveIds: configuration.selectedCogniveIds,
            overlay,
        };
        setPendingSavedInstallVersion((version) => version + 1);
        if (viewModeRef.current !== configuration.viewMode) {
            setViewModeLoading(true);
            setViewMode(configuration.viewMode);
        }
        editorActions.installConfiguration(configuration);
    }, [editorActions]);
    const forgetLoadedOptimizerPortfolio = useCallback(() => {
        pendingSavedInstallRef.current = null;
        setLoadedOptimizerPortfolio(null);
    }, []);
    // Remove the displayed result and make it unsaveable. The configuration and
    // the saved-portfolio selection are left to the caller.
    const clearDisplayedOptimizerResult = useCallback(() => {
        execution.clear();
        optimizerRunGenerationRef.current += 1;
        optimizerMutationGenerationRef.current += 1;
        resultActions.detachResult();
        setOptimizerIsRunning(false);
        setOptStatus(null);
        forgetLoadedOptimizerPortfolio();
    }, [execution.clear, forgetLoadedOptimizerPortfolio, resultActions]);
    // Edits keep a displayed result and mark it out of date. Only a saved
    // selection that has not been displayed yet is cancelled, so its install
    // cannot overwrite the edit.
    const clearReceiptProjectionForInputEdit = useCallback(() => {
        const pendingSavedSelection = receiptDisplaySource == null
            && listOfWhatIfPortfolios.some((portfolio) => (
                portfolio?.value === optimalPortfolioOptimizer
                && portfolio?.receiptStatus === 'available'
            ));
        if (!pendingSavedSelection) return;
        optimizerRunGenerationRef.current += 1;
        optimizerMutationGenerationRef.current += 1;
        resultActions.revokeSaveEligibility();
        setOptimizerIsRunning(false);
        setOptStatus(null);
        forgetLoadedOptimizerPortfolio();
        setOptimalPortfolioOptimizer('');
        setErrorOptimization('Saved optimizer inputs changed. Load or run a portfolio again.');
    }, [
        forgetLoadedOptimizerPortfolio,
        listOfWhatIfPortfolios,
        optimalPortfolioOptimizer,
        receiptDisplaySource,
        resultActions,
    ]);
    const setEditableConstraints = useCallback((nextConstraints) => {
        clearReceiptProjectionForInputEdit();
        editorActions.setConstraints(nextConstraints);
    }, [clearReceiptProjectionForInputEdit, editorActions]);
    const setEditableConstraintToggles = useCallback((nextToggles) => {
        clearReceiptProjectionForInputEdit();
        editorActions.setConstraintToggles(nextToggles);
    }, [clearReceiptProjectionForInputEdit, editorActions]);
    const setEditableGroupConstraints = useCallback((nextGroups) => {
        clearReceiptProjectionForInputEdit();
        editorActions.setGroupConstraints(nextGroups);
    }, [clearReceiptProjectionForInputEdit, editorActions]);
    const setEditableObjective = useCallback((nextObjective, rememberedSkill = null) => {
        clearReceiptProjectionForInputEdit();
        editorActions.chooseObjective(nextObjective, rememberedSkill);
    }, [clearReceiptProjectionForInputEdit, editorActions]);
    // Switching the baseline changes every row the result was built on, so the
    // result is cleared rather than kept as out of date.
    const setEditableViewMode = useCallback((nextViewMode) => {
        clearDisplayedOptimizerResult();
        setOptimalPortfolioOptimizer('');
        setViewModeLoading(true);
        setViewMode(nextViewMode);
    }, [clearDisplayedOptimizerResult]);
    const setEditableTickerData = useCallback((nextRows) => {
        const resolvedRows = typeof nextRows === 'function'
            ? nextRows(tickerDataRef.current)
            : nextRows;
        const normalizedRows = Array.isArray(resolvedRows) ? resolvedRows : [];
        clearReceiptProjectionForInputEdit();
        keepOptimizerSelection(viewModeRef.current, includedSelection(normalizedRows));
        setBaseRows(normalizedRows);
        tickerDataRef.current = normalizedRows;
        setTickerDataState(normalizedRows);
    }, [clearReceiptProjectionForInputEdit, keepOptimizerSelection]);
    const resolveActiveOptimizerContract = useCallback(async ({
        stagePublicationState = null, preparedTarget = null, isCurrentRequest = null,
    } = {}) => {
        const staging = typeof stagePublicationState === 'function';
        if (staging && (!preparedTarget || typeof isCurrentRequest !== 'function')) {
            throw new Error('Prepared Optimizer metadata requires an owned target.');
        }
        const userScope = optimizerUserScopeRef.current;
        const requestedWatchlistVersion = preparedTarget?.versions.watchlistVersion ?? watchlistVersion;
        const canAccept = () => optimizerUserScopeRef.current === userScope
            && optimizerWatchlistVersionRef.current === watchlistVersion
            && (!exclusiveCoreLoadOwnerRef?.current || staging)
            && (typeof isCurrentRequest !== 'function' || isCurrentRequest());
        if (!canAccept()) throw new Error('Optimizer capability request was superseded.');
        const { capability, catalog } = await getOrLoadOptimizerContract({
            userUid: userScope.key, watchlistVersion: requestedWatchlistVersion,
        });
        if (!canAccept()) throw new Error('Optimizer capability request was superseded.');
        const apply = () => {
            setOptimizerV2Capability(capability);
            setOptimizerAttributeCatalog(catalog);
            if (staging) {
                // Reconcile these visible editor definitions in the same reveal,
                // rather than letting the ordinary post-render effect do it later.
                const initializationKey = [userScope.key, catalog.revision_id, catalog.content_fingerprint].join(':');
                if (optimizerGroupInitializationRef.current !== initializationKey) {
                    optimizerGroupInitializationRef.current = initializationKey;
                    editorActions.reconcileGroups(catalog);
                }
                preparedContractScopeRef.current = { userScope, watchlistVersion: requestedWatchlistVersion };
            }
        };
        if (staging) stagePublicationState(apply, canAccept);
        else apply();
        return { capability, catalog };
    }, [watchlistVersion, exclusiveCoreLoadOwnerRef, editorActions]);
    const beginOptimizerOperation = () => {
        optimizerMutationGenerationRef.current += 1;
        return {
            generation: optimizerMutationGenerationRef.current,
            userScope: optimizerUserScopeRef.current,
        };
    };
    const isOptimizerOperationCurrent = (operation) => (
        optimizerMutationGenerationRef.current === operation.generation &&
        optimizerUserScopeRef.current === operation.userScope
    );
    const assertOptimizerOperationCurrent = (operation) => {
        if (!isOptimizerOperationCurrent(operation)) {
            throw new Error('Optimizer request was superseded by a newer operation.');
        }
    };
    const recordOptimizerMutationBarrier = useCallback((userScope, barrier) => {
        const normalizedBarrier = {
            portfolioId: barrier?.portfolioId ?? null,
            portfolioName: String(barrier?.portfolioName ?? '').trim(),
            portfolioVersion: barrier?.portfolioVersion ?? null,
            receiptId: barrier?.receiptId ?? null,
            expected: barrier?.expected,
        };
        const key = optimizerPortfolioBarrierKey(normalizedBarrier);
        if (!key || key === 'name:') return null;
        const currentState = optimizerPortfolioListStateRef.current;
        if (
            currentState.userUid === userScope.key &&
            currentState.mutationBarriers[key]
        ) {
            return null;
        }
        const revision = optimizerMutationBarrierRevisionRef.current + 1;
        optimizerMutationBarrierRevisionRef.current = revision;
        const nextState = commitOptimizerPortfolioListState(userScope, (previous) => ({
            ...previous,
            mutationBarriers: {
                ...previous.mutationBarriers,
                [key]: {
                    ...normalizedBarrier,
                    revision,
                    outcome: 'pending',
                    wasPresent: previous.portfolios.some(
                        (portfolio) => optimizerPortfolioMatchesBarrier(portfolio, normalizedBarrier),
                    ),
                },
            },
        }));
        return nextState ? { key, revision } : null;
    }, [commitOptimizerPortfolioListState]);
    const setOptimizerMutationBarrierOutcome = useCallback((
        userScope,
        barrierHandle,
        outcome,
        reconcileOnList = true,
        acknowledgement = null,
    ) => {
        let updated = false;
        commitOptimizerPortfolioListState(userScope, (previous) => {
            const barrier = previous.mutationBarriers[barrierHandle?.key];
            if (!barrier || barrier.revision !== barrierHandle?.revision) return previous;
            updated = true;
            return {
                ...previous,
                mutationBarriers: {
                    ...previous.mutationBarriers,
                    [barrierHandle.key]: {
                        ...barrier,
                        ...(acknowledgement ?? {}),
                        outcome,
                        reconcileOnList,
                    },
                },
            };
        });
        return updated;
    }, [commitOptimizerPortfolioListState]);
    const clearOptimizerMutationBarrier = useCallback((userScope, barrierHandle) => {
        let cleared = false;
        commitOptimizerPortfolioListState(userScope, (previous) => {
            const barrier = previous.mutationBarriers[barrierHandle?.key];
            if (!barrier || barrier.revision !== barrierHandle?.revision) return previous;
            cleared = true;
            const mutationBarriers = { ...previous.mutationBarriers };
            delete mutationBarriers[barrierHandle.key];
            return { ...previous, mutationBarriers };
        });
        return cleared;
    }, [commitOptimizerPortfolioListState]);

    const publishCompletedOptimizerRun = useCallback((publication) => {
        optimizerRunInputIdentityRef.current = publication.runInputIdentity;
        resultActions.publishRunReceipt(publication);
        setOptimalPortfolioOptimizer('Unsaved - Click "save" below!');
        setOptStatus('SUCCESS');
    }, [resultActions]);
    const [finalTickerDataFlag, setFinalTickerDataFlag] = useState(false);
    const currentRequestedFactorDataModel = useMemo(
        () => getEffectiveFactorDataModel(selectedFactorModel, selectedAxiomaModel, selectedBarraModel),
        [selectedAxiomaModel, selectedBarraModel, selectedFactorModel],
    );
    const buildCurrentOptimizerInputIdentity = useCallback((rows = tickerDataRef.current, useDisplayedRows = true,
        configuration = { constraints, constraintToggles, groupConstraints, objective }) => {
        const identityRows = useDisplayedRows && receiptDisplaySource != null && acceptedResultRef.current.receiptInputRows
            ? withLiveSelection(acceptedResultRef.current.receiptInputRows, rows)
            : rows;
        return buildOptimizerRunInputIdentity({
        userUid,
        activeView,
        viewMode,
        tickerData: identityRows,
        constraints: configuration.constraints,
        constraintToggles: configuration.constraintToggles,
        groupConstraints: configuration.groupConstraints,
        objective: configuration.objective,
        selectedFactorModel,
        selectedBetaSource,
        factorSnapshotDate,
        factorDataModel: factorDataModel ?? currentRequestedFactorDataModel,
        optimizerIdioVolSource,
        posSizingContextDocumentSha256:
            useDisplayedRows && receiptDisplaySource === 'run'
                ? acceptedResultRef.current.receiptPosSizingContextDigest
                : posSizingOptimizerContext?.context_document_sha256 ?? null,
        });
    }, [
        activeView,
        constraintToggles,
        constraints,
        groupConstraints,
        currentRequestedFactorDataModel,
        factorDataModel,
        objective,
        optimizerIdioVolSource,
        posSizingOptimizerContext?.context_document_sha256,
        receiptDisplaySource,
        selectedBetaSource,
        selectedFactorModel,
        factorSnapshotDate,
        userUid,
        viewMode,
    ]);
    const optimizerRunInputIdentity = useMemo(
        () => buildCurrentOptimizerInputIdentity(tickerData),
        [buildCurrentOptimizerInputIdentity, tickerData],
    );
    const optimizerCompletedResultIsCurrent = (
        receiptDisplaySource === 'run' &&
        displayedResultIdentity === optimizerRunInputIdentity &&
        completedOptimizerRun?.inputIdentity === optimizerRunInputIdentity
    );
    const currentUnsavedOptimizerRun = Boolean(optimizerCompletedResultIsCurrent);
    // A displayed result stays visible after an edit; it is out of date until
    // a rerun, or until the edit is reverted.
    const resultStale = receiptDisplaySource != null
        && displayedResultIdentity != null
        && displayedResultIdentity !== optimizerRunInputIdentity;

    useLayoutEffect(() => {
        if (optimizerRunInputIdentityRef.current !== optimizerRunInputIdentity) {
            optimizerRunInputIdentityRef.current = optimizerRunInputIdentity;
            // An in-flight run or save for the previous inputs can no longer
            // publish. A displayed result is kept and becomes out of date.
            optimizerMutationGenerationRef.current += 1;
            setOptimizerIsRunning(false);
        }
    }, [optimizerRunInputIdentity]);

    useEffect(() => {
        if (receiptDisplaySource === 'saved' && displayedResultIdentity == null) {
            resultActions.bindSavedIdentity(optimizerRunInputIdentity);
        }
    }, [displayedResultIdentity, optimizerRunInputIdentity, receiptDisplaySource, resultActions]);

    useLayoutEffect(() => {
        if (optimizerUiUserScopeKeyRef.current === optimizerUserScopeKey) {
            return;
        }
        optimizerUiUserScopeKeyRef.current = optimizerUserScopeKey;
        optimizerListRequestGenerationRef.current += 1;
        optimizerMutationGenerationRef.current += 1;
        optimizerRunGenerationRef.current += 1;
        invalidateOptimizerSummariesCache(optimizerUserScopeKey);
        const nextListState = {
            userUid: optimizerUserScopeKey,
            portfolios: [],
            error: null,
            mutationBarriers: {},
        };
        optimizerPortfolioListStateRef.current = nextListState;
        setOptimizerPortfolioListState(nextListState);
        resultActions.detachResult();
        setOptimizerV2Capability(null);
        setOptimizerAttributeCatalog(null);
        optimizerGroupInitializationRef.current = null;
        forgetLoadedOptimizerPortfolio();
        setOptimizerSelections(NO_OPTIMIZER_SELECTIONS);
        setBaseRows([]);
        tickerDataRef.current = [];
        setTickerDataState([]);
        editorActions.resetForUser(aum);
        setOptimalPortfolioOptimizer('');
        setOptStatus(null);
        setErrorOptimization(null);
        setOptimizerIsRunning(false);
    }, [
        aum,
        editorActions,
        forgetLoadedOptimizerPortfolio,
        optimizerUserScopeKey,
        resultActions,
    ]);

    const applyOptimizerLimitDefaults = useCallback(() => {
        editorActions.resetDefaults({ aum, gmv: checkedBaselineGmvRef.current });
    }, [aum, editorActions]);

    // Restore default limits and objective. A displayed result stays visible and
    // becomes out of date. If a saved selection is still loading, this edit
    // cancels it before it can overwrite the defaults.
    const resetOptimizerLimits = useCallback(() => {
        clearReceiptProjectionForInputEdit();
        applyOptimizerLimitDefaults();
    }, [applyOptimizerLimitDefaults, clearReceiptProjectionForInputEdit]);

    const resetOptimizerDefaults = useCallback(() => {
        clearDisplayedOptimizerResult();
        applyOptimizerLimitDefaults();
    }, [applyOptimizerLimitDefaults, clearDisplayedOptimizerResult]);

    const discardDesignExperiment = useCallback(async () => {
        if (META_OPTIMIZER_ENABLED) {
            await callBackend('optimizer_design/current', 'DELETE');
            setExperimentGeneration((value) => value + 1);
        }
        clearDisplayedOptimizerResult();
        setOptimalPortfolioOptimizer('');
        setErrorOptimization(null);
    }, [clearDisplayedOptimizerResult]);

    // New run discards unsaved choices in either workflow, keeping the setup.
    const detachOptimizerResult = useCallback(async () => {
        try { await discardDesignExperiment(); }
        catch (err) { setErrorOptimization(optimizerErrorMessage(err)); }
    }, [discardDesignExperiment]);

    const handleWhatIfPortfolioChange = (e) => {
        resetOptimizerDefaults();
        const [, name] = e.target.value;
        setErrorOptimization(null);
        setOptimalPortfolioOptimizer(name);
    };

    const handleAddConstraint = (factor, riskType) => {
        clearReceiptProjectionForInputEdit();
        editorActions.attachFactor(factor, riskType);
    };

    const handleRemoveConstraint = (key) => {
        clearReceiptProjectionForInputEdit();
        editorActions.removeFactor(key);
    };

    // A loaded portfolio keeps its saved AUM, like its other saved limits.
    useEffect(() => {
        if (receiptDisplaySource != null || loadedOptimizerPortfolio) return;
        editorActions.hydrateAum(aum);
    }, [aum, editorActions, loadedOptimizerPortfolio, receiptDisplaySource]);

    // Return the budget to the checked names' baseline GMV. Like any edit, this
    // cancels a saved selection that has not been displayed yet.
    const followCheckedGmvBudget = useCallback(() => {
        clearReceiptProjectionForInputEdit();
        editorActions.followCheckedGmv();
    }, [clearReceiptProjectionForInputEdit, editorActions]);
    const gmvBudget = useMemo(() => ({
        auto: gmvBudgetMode === 'auto',
        checkedGmv: checkedBaselineGmv,
        followChecked: followCheckedGmvBudget,
        markManual: markGmvBudgetManual,
    }), [checkedBaselineGmv, followCheckedGmvBudget, gmvBudgetMode, markGmvBudgetManual]);

    useEffect(() => {
        if (
            activeView !== 'optimizer'
            || !userUid
            || optimizerWorkspaceAvailable !== true
            || exclusiveCoreLoadOwnerRef?.current
        ) return undefined;
        if (preparedContractScopeRef.current?.userScope === optimizerUserScopeRef.current
            && preparedContractScopeRef.current.watchlistVersion === watchlistVersion) return undefined;
        let cancelled = false;
        void resolveActiveOptimizerContract().catch((error) => {
            if (!cancelled && !exclusiveCoreLoadOwnerRef?.current) {
                setErrorOptimization(
                    error?.message || 'Generic Optimizer constraints are unavailable.',
                );
            }
        });
        return () => {
            cancelled = true;
        };
    }, [
        activeView,
        optimizerWorkspaceAvailable,
        resolveActiveOptimizerContract,
        userUid,
        watchlistVersion,
        exclusiveCoreLoadOwnerRef,
    ]);

    useEffect(() => {
        if (optimizerWorkspaceAvailable !== false) return;
        optimizerGroupInitializationRef.current = null;
        setOptimizerV2Capability(null);
        setOptimizerAttributeCatalog(null);
        editorActions.clearGroups();
        setErrorOptimization(null);
    }, [editorActions, optimizerWorkspaceAvailable]);

    useEffect(() => {
        if (!optimizerV2Capability || !optimizerAttributeCatalog) return;
        const initializationKey = [
            optimizerUserScopeKey,
            optimizerAttributeCatalog.revision_id,
            optimizerAttributeCatalog.content_fingerprint,
        ].join(':');
        if (optimizerGroupInitializationRef.current === initializationKey) return;
        optimizerGroupInitializationRef.current = initializationKey;
        editorActions.reconcileGroups(optimizerAttributeCatalog);
    }, [
        editorActions,
        optimizerAttributeCatalog,
        optimizerUserScopeKey,
        optimizerV2Capability,
    ]);

    // Triggers action when the user chooses a new optimal portfolio to load from the dropdown
    useLoadSavedOptimizedPortfolio({
        whatIfPortfolio: optimalPortfolioOptimizer,
        watchlistVersion,
        unsavedOptimizerRun: currentUnsavedOptimizerRun,
        listOfWhatIfPortfolios,
        optimizerAttributeCatalog,
        setOptimizerIsRunning,
        installSavedOptimizerConfiguration,
        resetOptimizerDefaults,
        acceptSavedResult: resultActions.acceptSavedResult,
        setOptStatus,
        setErrorOptimization,
    });


    // Triggers actions when the user toggles the view mode between "current" and "pos-sizing"
    useViewModeBaseAndMetrics({
        activeView,
        viewMode,
        optimizerWorkspaceAvailable,
        industries,
        combinedHoldings,
        posSizingData,
        posSizingOptimizerContext,
        optimizerSelection: (
            optimizerUiUserScopeKeyRef.current === optimizerUserScopeKey
                ? optimizerSelections[viewMode] ?? null
                : null
        ),
        clearOptimizerSelection,

        setBaseRows,
        setErrorOptimization,
        setViewModeLoading,
        setFinalTickerDataFlag,
    });

    useLayoutEffect(() => {
        const nextTickerData = projectOptimizerTickerRows(
            baseRows,
            optimizedGmvsMap,
            receiptBaselineGmvsMap,
            acceptedResultRef.current.receiptInputRows,
        );
        tickerDataRef.current = nextTickerData;
        setTickerDataState(nextTickerData);
    }, [baseRows, optimizedGmvsMap, receiptBaselineGmvsMap]);

    // Once the loaded portfolio's baseline rows exist, check exactly its saved
    // names and show its saved result over those rows.
    useLayoutEffect(() => {
        const pending = pendingSavedInstallRef.current;
        if (
            !pending
            || viewMode !== pending.viewMode
            || viewModeLoading
            || baseRows.length === 0
        ) {
            return;
        }
        pendingSavedInstallRef.current = null;
        const selected = new Set(pending.selectedCogniveIds);
        const rows = baseRows.map((row) => {
            const checked = selected.has(normalizeCogniveId(row?.cognive_id));
            return row?.checkBoxEnable === checked ? row : { ...row, checkBoxEnable: checked };
        });
        keepOptimizerSelection(pending.viewMode, includedSelection(rows));
        setBaseRows(rows);
        installSavedReceiptAllocationOverlay(
            pending.overlay.allocations,
            pending.overlay.securityRows,
            rows,
        );
    }, [
        baseRows,
        installSavedReceiptAllocationOverlay,
        keepOptimizerSelection,
        pendingSavedInstallVersion,
        viewMode,
        viewModeLoading,
    ]);


    const fetchOptimizerPortfolios = useCallback(async ({
        force = false,
        isCurrentRequest = null,
        mutation = null,
        stagePublicationState = null,
        preparedTarget = null,
    } = {}) => {
        const staging = typeof stagePublicationState === 'function';
        if (staging && mutation) throw new Error('Prepared optimizer reads cannot mutate a save barrier.');
        const ownsPublicationRead = () => (
            (!exclusiveCoreLoadOwnerRef?.current || (staging && typeof isCurrentRequest === 'function'))
            && (typeof isCurrentRequest !== 'function' || isCurrentRequest())
        );
        if (!mutation && !ownsPublicationRead()) return { status: 'superseded' };
        const userScope = optimizerUserScope;
        if (!userScope.key) {
            return { status: 'skipped', reason: 'no_user' };
        }
        if (preparedTarget?.matrixStatus === 'empty_watchlist'
            || (!preparedTarget && optimizerWorkspaceAvailable === false)) {
            const apply = () => commitOptimizerPortfolioListState(userScope, (previous) => ({
                ...previous, portfolios: [], error: null,
            }));
            if (staging) stagePublicationState(apply, () => optimizerUserScopeRef.current === userScope
                && (typeof isCurrentRequest !== 'function' || isCurrentRequest()));
            else apply();
            return { status: 'skipped', reason: 'empty_watchlist' };
        }
        if (mutation?.mode === 'block') {
            if (optimizerUserScopeRef.current !== userScope) {
                return { status: 'superseded' };
            }
            const barrierHandle = recordOptimizerMutationBarrier(userScope, mutation.barrier);
            return barrierHandle
                ? { status: 'blocked', barrierHandle }
                : { status: 'error', code: 'invalid_optimizer_mutation_barrier' };
        }
        if (mutation?.mode === 'release') {
            if (optimizerUserScopeRef.current !== userScope) {
                return { status: 'superseded' };
            }
            return clearOptimizerMutationBarrier(userScope, mutation.barrierHandle)
                ? { status: 'released' }
                : { status: 'superseded' };
        }
        if (mutation?.mode === 'mark-unknown') {
            return setOptimizerMutationBarrierOutcome(
                userScope,
                mutation.barrierHandle,
                'unknown',
            )
                ? { status: 'blocked' }
                : { status: 'superseded' };
        }
        if (mutation?.mode === 'commit-and-reconcile') {
            if (!setOptimizerMutationBarrierOutcome(
                userScope,
                mutation.barrierHandle,
                'committed',
            )) {
                return { status: 'superseded' };
            }
        }

        const requestGeneration = optimizerListRequestGenerationRef.current + 1;
        optimizerListRequestGenerationRef.current = requestGeneration;
        const requestedWatchlistVersion = preparedTarget?.versions.watchlistVersion ?? watchlistVersion;
        const canPublish = () => (
            optimizerUserScopeRef.current === userScope &&
            optimizerListRequestGenerationRef.current === requestGeneration &&
            optimizerWatchlistVersionRef.current === watchlistVersion &&
            ownsPublicationRead()
        );

        if (!canPublish()) {
            return { status: 'superseded' };
        }

        try {
            const response = await getOrLoadOptimizerSummaries({
                userUid: userScope.key,
                watchlistVersion: requestedWatchlistVersion,
                force,
                loader: async () => {
                    const value = await callBackend(
                        'fetch_optimizer_portfolio_summaries_v2',
                        'POST',
                        optimizerV2VersionCommand(),
                    );
                    requireOptimizerV2PortfolioSummariesResponse(value);
                    return value;
                },
            });
            const portfolios = requireOptimizerPortfolioList(response);
            if (!canPublish()) {
                return { status: 'superseded' };
            }
            const reconciledBarriers = [];
            const applyList = () => {
                commitOptimizerPortfolioListState(userScope, (previous) => {
                    const mutationBarriers = { ...previous.mutationBarriers };
                    Object.entries(mutationBarriers).forEach(([key, barrier]) => {
                        if (optimizerMutationBarrierIsReconciled(barrier, portfolios, force)) {
                            delete mutationBarriers[key];
                            reconciledBarriers.push({ key, revision: barrier.revision });
                        }
                    });
                    return {
                        ...previous,
                        portfolios,
                        error: null,
                        mutationBarriers,
                    };
                });
            };
            if (staging) stagePublicationState(applyList, canPublish);
            else applyList();
            const reconciliationHandle = mutation?.barrierHandle ?? null;
            return reconciliationHandle
                ? {
                    ...response,
                    optimizerMutationReconciled: reconciledBarriers.some((barrier) => (
                        barrier.key === reconciliationHandle.key &&
                        barrier.revision === reconciliationHandle.revision
                    )),
                }
                : response;
        } catch (error) {
            if (!canPublish()) return { status: 'superseded' };
            const message = `Failed to load saved optimizer portfolios. ${optimizerErrorMessage(
                error,
                'Optimizer portfolio list refresh failed.',
            )}`;
            const applyError = () => commitOptimizerPortfolioListState(userScope, (previous) => ({
                ...previous, error: message,
            }));
            if (staging) stagePublicationState(applyError, canPublish);
            else applyError();
            throw error;
        }
    }, [
        commitOptimizerPortfolioListState,
        clearOptimizerMutationBarrier,
        optimizerWorkspaceAvailable,
        optimizerUserScope,
        recordOptimizerMutationBarrier,
        setOptimizerMutationBarrierOutcome,
        watchlistVersion,
        exclusiveCoreLoadOwnerRef,
    ]);

    const { saveOptimizationAndWhatIfPortfolioData, refreshSavedOptimizedPortfolio } = useOptimizerPortfolioPersistence({
        operations: { begin: beginOptimizerOperation, isCurrent: isOptimizerOperationCurrent,
            assertCurrent: assertOptimizerOperationCurrent },
        catalogue: { portfolios: listOfWhatIfPortfolios, recordBarrier: recordOptimizerMutationBarrier,
            setBarrierOutcome: setOptimizerMutationBarrierOutcome, clearBarrier: clearOptimizerMutationBarrier,
            refresh: fetchOptimizerPortfolios },
        results: {
            captureSavableRun: () => {
                // Read the accepted model synchronously: an acknowledgement or
                // unknown outcome consumes eligibility before React rerenders.
                const completedRun = acceptedResultRef.current.completedOptimizerRun;
                const identity = buildCurrentOptimizerInputIdentity(tickerDataRef.current);
                return completedRun && receiptDisplaySource === 'run'
                    && displayedResultIdentity === identity && completedRun.inputIdentity === identity
                    && completedRun.receiptId ? completedRun : null;
            },
            revokeSaveEligibility: resultActions.revokeSaveEligibility,
            selectSavedPortfolio: setOptimalPortfolioOptimizer,
        },
        workspace: { resolveContract: resolveActiveOptimizerContract, posSizingOptimizerContext,
            onPosSizingContextChanged },
        feedback: {
            begin: () => { setOptimizerIsRunning(true); setErrorOptimization(null); },
            error: setErrorOptimization,
            finish: () => setOptimizerIsRunning(false),
        },
        execution,
    });

    const runOptimization = () => execution.runOptimization({
        operations: {
            beginRun: () => {
                const operation = beginOptimizerOperation();
                const runGeneration = ++optimizerRunGenerationRef.current;
                return { operation, runGeneration };
            },
            bindRunIdentity: (identity) => { optimizerRunInputIdentityRef.current = identity; },
            ownsRun: (operation, generation, identity) => (
                optimizerRunGenerationRef.current === generation
                && optimizerRunInputIdentityRef.current === identity
                && isOptimizerOperationCurrent(operation)
            ),
        },
        workspace: {
            captureRun: () => {
                const runInputRows = withLiveSelection(baseRows, tickerDataRef.current);
                return { runInputRows,
                    runInputIdentity: buildCurrentOptimizerInputIdentity(runInputRows, false),
                    viewMode, constraints, constraintToggles, groupConstraints, objective, posSizingOptimizerContext };
            },
            resolveContract: resolveActiveOptimizerContract,
            onPosSizingContextChanged,
        },
        results: { currentUnsavedRun: currentUnsavedOptimizerRun,
            completedSourceKind: completedOptimizerRun?.source?.kind, publishRun: publishCompletedOptimizerRun },
        feedback: {
            begin: () => {
                setOptimalPortfolioOptimizer('');
                setErrorOptimization(null);
                setOptStatus(null);
                setOptimizerIsRunning(true);
            },
            error: setErrorOptimization,
            fail: (message, status) => { setErrorOptimization(message); setOptStatus(status); },
            finish: () => setOptimizerIsRunning(false),
        },
        experiment: { started: () => {
            setExperimentGeneration((value) => value + 1);
            resultActions.revokeSaveEligibility();
            forgetLoadedOptimizerPortfolio();
            clearOptimizedOverlay();
        } },
    });
    const optimizerRunBlocker = viewMode === 'pos-sizing'
        ? getPosSizingOptimizerRunBlocker(posSizingOptimizerContext)
        : null;
    const buildDesignCommand = useCallback(async (draft, { resolvedContract = null, posSizingContext = posSizingOptimizerContext } = {}) => {
        const { capability, catalog } = resolvedContract ?? await resolveActiveOptimizerContract();
        const availableGroups = filterGroupConstraintsToCatalog(draft.groupConstraints, catalog);
        if (availableGroups.length !== draft.groupConstraints.length) {
            throw new Error('An attribute group in this design is no longer available. Review its limits before starting a new study.');
        }
        const configuration = {
            baselineSourceMode: draft.baselineSourceMode,
            selectedCogniveIds: [...new Set(draft.selectedCogniveIds)].sort(),
            // Meta supplies its construction document through study settings.
            constraints: { ...draft.constraints, design: undefined },
            constraintToggles: draft.constraintToggles,
            groupConstraints: availableGroups,
            objective: 'AlphaScore',
        };
        const error = optimizerRunPreflightError({ ...configuration, capability, posSizingOptimizerContext: posSizingContext });
        if (error) throw new Error(error);
        return buildOptimizerRunCommand({ ...configuration,
            posSizingContextDocumentSha256: draft.baselineSourceMode === 'pos_sizing'
                ? posSizingContext?.context_document_sha256 : null,
            runOperationId: draft.runOperationId,
        });
    }, [resolveActiveOptimizerContract, posSizingOptimizerContext]);

    const baselineIds = baseRows.filter((row) => row.checkBoxEnable === true).map((row) => normalizeCogniveId(row.cognive_id)).filter(Boolean).sort();
    const baselineInputKey = buildOptimizerBaselineInputKey({ userScopeKey: optimizerUserScopeKey,
        watchlistVersion, viewMode, selectedFactorModel, selectedBetaSource, factorSnapshotDate, factorDataModel,
        optimizerIdioVolSource, selectedAxiomaModel, selectedBarraModel, posSizingOptimizerContext,
        aum: constraints.static_constraints.aum, gmv: constraints.static_constraints.gmv, rows: baseRows,
        factorSliceScopeKey });
    const liveBaseline = useOptimizerBaseline({
        enabled: activeView === 'optimizer' && Boolean(userUid) && optimizerWorkspaceAvailable
            && finalTickerDataFlag && !viewModeLoading && !optimizerRunBlocker
            && baselineIds.length > 0 && constraints.static_constraints.aum > 0 && constraints.static_constraints.gmv > 0,
        inputKey: baselineInputKey,
        canRead: () => !exclusiveCoreLoadOwnerRef?.current,
        buildCommand: () => buildDesignCommand({
            baselineSourceMode: optimizerBaselineForViewMode(viewMode), selectedCogniveIds: baselineIds,
            constraints: buildOptimizerDefaultConstraints(constraints.static_constraints.aum, constraints.static_constraints.gmv),
            constraintToggles: { static_constraints: { gmv: true } }, groupConstraints: [],
            runOperationId: createOptimizerOperationId(),
        }),
    });
    const publicationInputsRef = useRef(null);
    publicationInputsRef.current = { activeView, userScope: optimizerUserScope, factorSliceScopeKey, watchlistVersion,
        viewMode, editorState, optimizerSelections, combinedHoldings, industries, posSizingData, posSizingOptimizerContext,
        loadedOptimizerPortfolio, receiptDisplaySource, acceptedResult: acceptedResultRef.current, aum,
        selectedFactorModel, selectedBetaSource, factorSnapshotDate, factorDataModel, optimizerIdioVolSource,
        selectedAxiomaModel, selectedBarraModel, optimizerWorkspaceAvailable };
    const prepareOptimizerPublication = useCallback(async (options) => {
        const { preparedTarget: target, stagePublicationState: stage, isCurrentRequest } = options;
        if (!target || typeof stage !== 'function' || typeof isCurrentRequest !== 'function') {
            throw new Error('Prepared Optimizer baseline requires an owned publication target.');
        }
        const captured = publicationInputsRef.current;
        const isCurrent = () => isCurrentRequest() && Object.keys(captured).every(key => (
            Object.is(publicationInputsRef.current[key], captured[key])
        ));
        if (!isCurrent() || captured.activeView !== 'optimizer') throw new Error('Optimizer baseline preparation was superseded.');
        const next = target.optimizerInputs;
        const required = ['selectedFactorModel', 'selectedBetaSource', 'factorSnapshotDate', 'factorDataModel',
            'optimizerIdioVolSource', 'selectedAxiomaModel', 'selectedBarraModel', 'aum'];
        if (!next || required.some(key => !Object.prototype.hasOwnProperty.call(next, key))
            || !Array.isArray(target.actualRows) || !Array.isArray(target.watchlistRows)
            || !Number.isFinite(target.versions?.watchlistVersion)) {
            throw new Error('Prepared Optimizer baseline inputs are incomplete.');
        }
        const workspace = target.workspace;
        const retainSavedChild = target.childDecision?.unchanged === true
            || target.childDecision?.deferred === true || target.childDecision?.awaitingAcceptance === true;
        if (!workspace && !retainSavedChild) throw new Error('Prepared Optimizer child decision is missing.');
        if (workspace && !Array.isArray(workspace.pos_sizing)) throw new Error('Prepared Optimizer child rows are missing.');
        const nextPosData = workspace ? workspace.pos_sizing : captured.posSizingData;
        const nextPosContext = workspace ? workspace.pos_sizing_sync_status?.optimizer_context ?? null : captured.posSizingOptimizerContext;
        const selection = optimizerUiUserScopeKeyRef.current === optimizerUserScopeKey
            ? captured.optimizerSelections[captured.viewMode] ?? null : null;
        const projection = projectOptimizerBaseRows({ viewMode: captured.viewMode,
            optimizerWorkspaceAvailable: target.watchlistRows.length > 0, industries: target.watchlistRows,
            combinedHoldings: target.actualRows, posSizingData: nextPosData, posSizingOptimizerContext: nextPosContext,
            optimizerSelection: selection, previousActualMembershipSignature: actualMembershipSignature(captured.combinedHoldings) });
        if (!projection.ready || projection.baselineIdentityError) {
            throw new Error(projection.baselineIdentityError || 'Prepared Optimizer baseline is unavailable.');
        }
        const rows = projection.rows;
        // A displayed immutable run keeps its recorded GMV policy, just as the
        // existing live editor does. No receipt or result context is rewritten.
        const gmvRows = captured.receiptDisplaySource != null && captured.acceptedResult.receiptInputRows
            ? withLiveSelection(captured.acceptedResult.receiptInputRows, rows) : rows;
        const nextGmv = captured.editorState.gmvBudgetMode === 'auto'
            ? checkedOptimizerBaselineGmv(gmvRows) : captured.editorState.constraints.static_constraints.gmv;
        const nextAum = captured.receiptDisplaySource != null || captured.loadedOptimizerPortfolio
            ? captured.editorState.constraints.static_constraints.aum : next.aum;
        const ids = rows.filter(row => row.checkBoxEnable === true)
            .map(row => normalizeCogniveId(row.cognive_id)).filter(Boolean).sort();
        const inputKey = buildOptimizerBaselineInputKey({ ...next, userScopeKey: optimizerUserScopeKey,
            watchlistVersion: target.versions.watchlistVersion, viewMode: captured.viewMode,
            posSizingOptimizerContext: nextPosContext, aum: nextAum, gmv: nextGmv, rows,
            factorSliceScopeKey: captured.factorSliceScopeKey });
        const emptyWatchlist = target.watchlistRows.length === 0;
        const resolvedContract = emptyWatchlist ? null
            : await resolveActiveOptimizerContract({ ...options, isCurrentRequest: isCurrent });
        if (!isCurrent()) throw new Error('Optimizer baseline preparation was superseded.');
        if (emptyWatchlist) stage(() => {
            optimizerGroupInitializationRef.current = null;
            setOptimizerV2Capability(null);
            setOptimizerAttributeCatalog(null);
            editorActions.clearGroups();
            setErrorOptimization(null);
        }, isCurrent);
        const blocker = captured.viewMode === 'pos-sizing' ? getPosSizingOptimizerRunBlocker(nextPosContext) : null;
        const enabled = projection.finalTickerDataFlag && !blocker && ids.length > 0 && nextAum > 0 && nextGmv > 0;
        await liveBaseline.prepare({ inputKey, enabled, isCurrentRequest: isCurrent, stagePublicationState: stage,
            buildCommand: () => buildDesignCommand({ baselineSourceMode: optimizerBaselineForViewMode(captured.viewMode),
                selectedCogniveIds: ids, constraints: buildOptimizerDefaultConstraints(nextAum, nextGmv),
                constraintToggles: { static_constraints: { gmv: true } }, groupConstraints: [],
                runOperationId: createOptimizerOperationId() }, { resolvedContract, posSizingContext: nextPosContext }) });
        if (!isCurrent()) throw new Error('Optimizer baseline preparation was superseded.');
        stage(() => {
            if (projection.discardKeptSelection) clearOptimizerSelection('normal');
            setBaseRows(rows);
            setFinalTickerDataFlag(projection.finalTickerDataFlag);
            setViewModeLoading(false);
            if (captured.receiptDisplaySource == null && !captured.loadedOptimizerPortfolio) editorActions.hydrateAum(nextAum);
            if (captured.editorState.gmvBudgetMode === 'auto') editorActions.syncAutomaticGmv(nextGmv);
        }, isCurrent);
        return { inputKey };
    }, [optimizerUserScopeKey, resolveActiveOptimizerContract, liveBaseline.prepare,
        buildDesignCommand, clearOptimizerSelection, editorActions]);
    const baselineData = liveBaseline.data;
    // Ordinary v7 and Max Sharpe v9 receipts retain their historical score
    // display; v8 design results report the actual specific-risk-weighted
    // objective coefficients.
    const baselinePortfolioMetrics = baselineData?.risk_metrics.act_port == null ? null
        : constraints.design || (objective !== 'AlphaScore' && objective !== MAX_SHARPE_OBJECTIVE)
            ? { ...baselineData.risk_metrics.act_port, total_alpha_score: baselineData.metrics.alpha_score }
            : baselineData.risk_metrics.act_port;
    const displayedEvaluation = receiptDisplaySource ? constraintEvaluation : baselineData?.constraint_evaluation ?? null;
    const displayedAttributes = receiptDisplaySource ? attributeRiskMetrics : baselineData?.attribute_risk_metrics ?? null;
    const optimizerAttributeWorkspace = optimizerAttributeCatalog == null
        ? null
        : {
            ...optimizerAttributeCatalog,
            risk_results: displayedAttributes,
        };
    async function selectDesignCandidate({ studyId, index, stage = null, isCurrent = () => true }) {
        const operation = beginOptimizerOperation();
        const runGeneration = ++optimizerRunGenerationRef.current;
        const previousIdentity = buildCurrentOptimizerInputIdentity(tickerDataRef.current);
        optimizerRunInputIdentityRef.current = previousIdentity;
        const ownsRun = () => isOptimizerOperationCurrent(operation)
            && optimizerRunGenerationRef.current === runGeneration
            && optimizerRunInputIdentityRef.current === previousIdentity;
        const current = () => isCurrent() && ownsRun();
        setOptimizerIsRunning(true);
        try {
            const raw = await callBackend(`optimizer_design/studies/${encodeURIComponent(studyId)}/select`, 'POST', canonicalJsonObject({ trial_index: index, stage }));
            const receiptResponse = requireOptimizerV2RunResponse(raw);
            if (!current()) return false;
            if (raw.optimizer_run_baseline?.source_mode !== optimizerBaselineForViewMode(viewMode)) {
                throw new Error('Switch the shared baseline to the baseline used by this study before selecting its portfolio.');
            }
            const configuration = optimizerReceiptRunConfiguration(receiptResponse,
                raw.optimizer_run_baseline.source_mode, { catalog: optimizerAttributeCatalog });
            const selectedIds = new Set(receiptResponse.securityRows.map((row) => row.cognive_id));
            const runInputRows = baseRows.map((row) => ({ ...row, checkBoxEnable: selectedIds.has(normalizeCogniveId(row.cognive_id)) }));
            const runInputIdentity = buildCurrentOptimizerInputIdentity(runInputRows, false, configuration);
            editorActions.installConfiguration(configuration);
            setPendingSavedInstallVersion((version) => version + 1);
            keepOptimizerSelection(viewMode, includedSelection(runInputRows));
            setBaseRows(runInputRows);
            forgetLoadedOptimizerPortfolio();
            publishCompletedOptimizerRun({ receiptResponse, runInputRows, runInputIdentity, runGeneration,
                posSizingContextDigest: viewMode === 'pos-sizing' ? posSizingOptimizerContext?.context_document_sha256 : null,
                source: { kind: 'meta', studyId, index, stage },
            });
            setErrorOptimization(null);
            setOptimizerIsRunning(false);
            return true;
        } finally {
            if (ownsRun()) setOptimizerIsRunning(false);
        }
    }

    return {
        riskMetricsActPort: receiptDisplaySource ? riskMetricsActPort : baselinePortfolioMetrics,
        riskMetricsOptPort,
        riskMetricsDeltaPort,
        displayedExpectedReturnPolicy: receiptDisplaySource ? displayedExpectedReturnPolicy : null,
        optimizerIsRunning,
        optimizerQueueState: execution.queueState,
        cancelQueuedOptimization: execution.cancel,
        optimizerRunBlocker,
        errorOptimization: errorOptimization ?? optimizerPortfolioStateForUser.error,
        setErrorOptimization,
        runOptimization,
        metaOptimizer: {
            design: {
                scopeKey: optimizerUserScopeKey,
                savedConfigurationVersion: pendingSavedInstallVersion,
                baseline: baselineData,
                baselineLoading: liveBaseline.loading,
                baselineError: liveBaseline.error,
                retryBaseline: liveBaseline.retry,
                experimentGeneration,
                discardExperiment: discardDesignExperiment,
                clearCurrentResult: clearDisplayedOptimizerResult,
                buildCommand: buildDesignCommand,
                selectCandidate: selectDesignCandidate,
                currentResult: completedOptimizerRun?.source ?? null,
                currentRunOperationId: receiptDisplaySource === 'saved'
                    ? loadedOptimizerPortfolio?.runOperationId ?? null
                    : completedOptimizerRun?.runOperationId ?? null,
                canSaveCurrent: currentUnsavedOptimizerRun,
                initialDraft: {
                    baselineSourceMode: optimizerBaselineForViewMode(viewMode),
                    selectedCogniveIds: withLiveSelection(baseRows, tickerDataRef.current)
                        .filter((row) => row.checkBoxEnable === true)
                        .map((row) => normalizeCogniveId(row.cognive_id)).filter(Boolean).sort(),
                    constraints, constraintToggles, groupConstraints,
                },
            },

        },
        constraints, setConstraints: setEditableConstraints,
        gmvBudget,
        groupConstraints,
        setGroupConstraints: setEditableGroupConstraints,
        optimizerV2Capability,
        optimizerAttributeCatalog: optimizerAttributeWorkspace,
        handleWhatIfPortfolioChange,
        optimalPortfolioOptimizer: optimalPortfolioOptimizer,
        listOfWhatIfPortfolios,
        readWhatIfPortfolios,
        saveOptimizationAndWhatIfPortfolioData,
        fetchOptimizerPortfolios,
        resolveActiveOptimizerContract,
        prepareOptimizerPublication,
        constraintToggles, setConstraintToggles: setEditableConstraintToggles,
        constraintSummary,
        showPortfolioMetrics: showPortfolioMetrics || Boolean(baselineData),
        tickerData,
        setTickerData: setEditableTickerData,
        objective, setObjective: setEditableObjective,
        setShowPortfolioMetrics,
        setOptimalPortfolioOptimizer: setOptimalPortfolioOptimizer,
        clearOptimizedOverlay,
        unsavedOptimizerRun: currentUnsavedOptimizerRun,
        receiptDisplayActive: receiptDisplaySource != null,
        loadedOptimizerFactorContext: loadedFactorContext,
        loadedOptimizerFactorResult: loadedFactorResult,
        resultStale,
        detachOptimizerResult,
        resetOptimizerLimits,
        optStatus,
        viewMode, setViewMode: setEditableViewMode,
        risksByFactor: receiptDisplaySource ? risksByFactor : baselineData?.risks_by_factor ?? null,
        constraintEvaluation: displayedEvaluation,
        resultIssuedAt,
        solverStatus,
        handleAddConstraint,
        handleRemoveConstraint,
        viewModeLoading,
        finalTickerDataFlag,
        resetOptimizerDefaults,
        refreshSavedOptimizedPortfolio,
    };
}
